import outdent from "outdent";
import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const microsoftSentinel = defineConnector({
  identifier: "microsoftSentinel",
  name: "Microsoft Sentinel",
  tagLine: "Cloud-native SIEM and SOAR",
  keywords:
    "azure sentinel siem soar security insights log analytics incidents",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: () => "https://management\\.azure\\.com/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Create an Azure app registration",
      items: [
        {
          kind: "markdown",
          content: outdent`
            1. Navigate to your [Azure app registrations](https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade) and select **New registration**
            2. **Name**: give your app a descriptive name
            3. **Supported account types**: select 'Single tenant only'
            4. **Redirect URI**: leave this blank
            5. Select **Register**
          `,
        },
      ],
    },
    {
      title: "Get client ID and tenant ID",
      items: [
        {
          kind: "markdown",
          content:
            "Navigate to the **Overview** of your created app and copy the following values:",
        },
        {
          kind: "input",
          label: "Application (client) ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
          placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        },
        {
          kind: "input",
          label: "Directory (tenant) ID",
          required: true,
          target: { kind: "configuration", field: "tenantId" },
          placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        },
      ],
    },
    {
      title: "Create a client secret",
      items: [
        {
          kind: "markdown",
          content: outdent`
            1. Select **Certificates & secrets**, then **+ New client secret**
            2. Enter a description and select your preferred expiry
            3. Select **Add**
            4. Copy the **value** of the secret
          `,
        },
        {
          kind: "input",
          label: "Client secret value",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Grant access to the workspace",
      items: [
        {
          kind: "markdown",
          content: outdent`
            In the Azure portal, open your Microsoft Sentinel workspace, go to **Access control (IAM)**, and assign the app the **Microsoft Sentinel Responder** (or **Reader**) role so it can read and update incidents.
          `,
        },
      ],
    },
    {
      title: "Identify your workspace",
      items: [
        {
          kind: "input",
          label: "Subscription ID",
          required: true,
          target: { kind: "configuration", field: "subscriptionId" },
          placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        },
        {
          kind: "input",
          label: "Resource group name",
          required: true,
          target: { kind: "configuration", field: "resourceGroupName" },
        },
        {
          kind: "input",
          label: "Workspace name",
          required: true,
          target: { kind: "configuration", field: "workspaceName" },
          helperText: "The Log Analytics workspace that backs Sentinel.",
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    tokenEndpoint: ({ tenantId }) =>
      `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`,
    scopes: () => ["https://management.azure.com/.default"],
    clientCredentialsFrom: "db",
    tokenEndpointAuthMethod: () => "client_secret_post",
  },
  connectionTest: {
    protocol: "http",
    request: ({ subscriptionId, resourceGroupName, workspaceName }) => ({
      url: `https://management.azure.com/subscriptions/${subscriptionId}/resourceGroups/${resourceGroupName}/providers/Microsoft.OperationalInsights/workspaces/${workspaceName}/providers/Microsoft.SecurityInsights/incidents?api-version=2025-09-01&$top=1`,
      expectedStatus: [200, 403],
    }),
  },
});
